import React, { useState } from "react";
import { FileVisibility } from "../services/filesService";
import { shareUrl } from "../data/shareRouting";
import { copyTextToClipboard } from "../data/clipboard";
import { LinkIcon } from "../../packages/excalidraw/components/icons";
import { glyph } from "./shareIcons";
import { ManualCopyField } from "./ManualCopyField";

/**
 * "Embed in Notion" guided flow — the launch's user-education moment. It lives
 * in the Share popover's extension slot and walks the user through the one
 * requirement embeds have: the mindmap must be public.
 *
 * State machine:
 *   idle ──click──▶ (private?) confirming : (copy) ready | manual
 *   confirming ──Make public──▶ (copy) ready | manual   (persists first, THEN copies)
 *   confirming ──Cancel──────▶ idle                    (visibility unchanged)
 *   manual ──Continue──▶ ready                (user copied it by hand)
 *
 * "Embedding" is a plain URL paste — there is no Notion API call here. Once
 * the file is public we copy the canonical `/share/<id>` URL (never the
 * editor URL). The state only advances to "ready" (paste instructions) when
 * that copy actually succeeded — the Clipboard API isn't available
 * everywhere (non-secure origins, older Safari) — otherwise we land on
 * "manual" and show the URL selectable so the user can copy it themselves
 * before continuing.
 */
type FlowState = "idle" | "confirming" | "manual" | "ready";

export const EmbedInNotion: React.FC<{
  fileId: string;
  visibility: FileVisibility;
  /** Persists visibility (owner-only via RLS). Used to make a private file public. */
  onSetVisibility: (next: FileVisibility) => Promise<unknown>;
  /** Origin override for tests; defaults to the current window origin. */
  origin?: string;
}> = ({ fileId, visibility, onSetVisibility, origin }) => {
  const [state, setState] = useState<FlowState>("idle");
  const [error, setError] = useState<string | null>(null);

  // Copy the link, then advance only on success. On failure we land on
  // "manual" instead of "ready" — the paste instructions assume the link is
  // already on the clipboard, which would be a lie if the copy failed.
  const proceed = async () => {
    const url = shareUrl(fileId, origin);
    const ok = await copyTextToClipboard(url);
    setState(ok ? "ready" : "manual");
  };

  const handleStart = () => {
    setError(null);
    if (visibility === "public") {
      void proceed();
    } else {
      setState("confirming");
    }
  };

  const handleConfirm = () => {
    setError(null);
    // Persist FIRST, copy only on success. Copying before the file is actually
    // public would put a link on the clipboard that 404s for the recipient if
    // the visibility write fails — so we await the write and only then proceed
    // to copy + instructions. On failure we stay on the prompt with an error.
    Promise.resolve(onSetVisibility("public"))
      .then(() => {
        void proceed();
      })
      .catch((err) => {
        setState("confirming");
        setError("Couldn't make it public. Try again.");
        // eslint-disable-next-line no-console
        console.error(err);
      });
  };

  const handleCancel = () => {
    setError(null);
    setState("idle");
  };

  if (state === "confirming") {
    return (
      <div
        data-testid="embed-in-notion"
        style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
      >
        <span style={{ fontSize: "0.85rem" }}>
          Embeds need this mindmap public — make it public?
        </span>
        {error && (
          <span role="alert" style={{ fontSize: "0.8rem", color: "#d92d20" }}>
            {error}
          </span>
        )}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            data-testid="embed-in-notion-confirm"
            onClick={handleConfirm}
            style={primaryBtn}
          >
            Make public
          </button>
          <button
            type="button"
            data-testid="embed-in-notion-cancel"
            onClick={handleCancel}
            style={secondaryBtn}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (state === "manual") {
    return (
      <div
        data-testid="embed-in-notion"
        style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}
      >
        <span style={{ fontSize: "0.85rem", color: "#d92d20" }}>
          Couldn't copy automatically — copy the link below, then continue.
        </span>
        <ManualCopyField
          data-testid="embed-in-notion-manual-input"
          value={shareUrl(fileId, origin)}
        />
        <button
          type="button"
          data-testid="embed-in-notion-manual-continue"
          onClick={() => setState("ready")}
          style={{ ...primaryBtn, alignSelf: "flex-start" }}
        >
          I copied it — continue
        </button>
      </div>
    );
  }

  if (state === "ready") {
    return (
      <div
        data-testid="embed-in-notion"
        style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}
      >
        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
          Link copied — now add it to Notion:
        </span>
        <ol
          style={{
            margin: 0,
            paddingLeft: "1.1rem",
            fontSize: "0.82rem",
            color: "#4b5563",
            display: "flex",
            flexDirection: "column",
            gap: "0.15rem",
          }}
        >
          <li>Paste the link into Notion.</li>
          <li>Choose “Embed” when Notion asks.</li>
        </ol>
        <span
          data-testid="embed-in-notion-hint"
          style={{ fontSize: "0.8rem", color: "#6b7280" }}
        >
          Tip: in Notion you can also type <code>/embed</code> first, then paste
          the link.
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="embed-in-notion-start"
      onClick={handleStart}
      style={{
        ...secondaryBtn,
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
        width: "100%",
        justifyContent: "center",
      }}
    >
      {glyph(LinkIcon)}
      Embed in Notion
    </button>
  );
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 0.75rem",
  borderRadius: "0.5rem",
  border: "none",
  background: "#6965db",
  color: "#fff",
  fontWeight: 600,
  fontSize: "0.82rem",
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  padding: "0.45rem 0.75rem",
  borderRadius: "0.5rem",
  border: "1px solid var(--default-border-color, #e5e5ea)",
  background: "transparent",
  color: "var(--text-primary-color, #1b1b1f)",
  fontWeight: 600,
  fontSize: "0.82rem",
  cursor: "pointer",
};

export default EmbedInNotion;
